import {
  Globe,
  Linkedin,
  Phone,
  MapPin,
  Building2,
  Info,
  Calendar,
  UserCheck,
  DollarSign,
  ExternalLink,
} from "lucide-react";
import {
  useGetIdentity,
  useLocaleState,
  useRecordContext,
  useTranslate,
  useRefresh,
} from "ra-core";
import { DeleteButton } from "@/components/admin/delete-button";
import { formatLocalizedDate } from "../misc/RelativeDate";
import { DetailSidePanelSection } from "../misc/DetailSidePanelSection";
import { InlineEditField } from "../misc/InlineEditField";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { getTranslatedCompanySizeLabel } from "./getTranslatedCompanySizeLabel";
import { sizes } from "./sizes";
import { useGetSalesName } from "../sales/useGetSalesName";

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  if (!record) return null;

  return (
    <div className="space-y-3">
      <CompanyContactInfo record={record} />
      <CompanyAddressInfo record={record} />
      <CompanyContextInfo record={record} />
      <CompanyAdditionalInfo record={record} />

      {link !== "edit" && (
        <div className="pt-2 flex flex-col gap-2">
          <DeleteButton
            className="w-full justify-center h-8 cursor-pointer hover:bg-destructive/10! text-destructive! border border-destructive/40 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

export const CompanyContactInfo = ({ record }: { record: Company }) => {
  const refresh = useRefresh();

  return (
    <DetailSidePanelSection
      title="Contato & Comunicação"
      icon={<Globe className="w-4 h-4 text-primary" />}
      defaultOpen={true}
    >
      <div className="space-y-1">
        <InlineEditField
          label="Telefone"
          field="phone_number"
          value={record.phone_number}
          resource="companies"
          recordId={record.id}
          previousData={record}
          type="tel"
          onSaved={refresh}
          displayValue={
            record.phone_number ? (
              <span className="flex items-center gap-1.5 font-medium text-foreground">
                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                {record.phone_number}
              </span>
            ) : undefined
          }
        />

        <InlineEditField
          label="Website"
          field="website"
          value={record.website}
          resource="companies"
          recordId={record.id}
          previousData={record}
          type="text"
          onSaved={refresh}
          displayValue={
            record.website ? (
              <a
                href={
                  record.website.startsWith("http")
                    ? record.website
                    : `https://${record.website}`
                }
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-primary hover:underline inline-flex items-center gap-1 truncate max-w-[180px]"
              >
                <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                {record.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            ) : undefined
          }
        />

        <InlineEditField
          label="LinkedIn"
          field="linkedin_url"
          value={record.linkedin_url}
          resource="companies"
          recordId={record.id}
          previousData={record}
          type="text"
          onSaved={refresh}
          displayValue={
            record.linkedin_url ? (
              <a
                href={record.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-primary hover:underline inline-flex items-center gap-1"
              >
                <Linkedin className="w-3.5 h-3.5 text-muted-foreground" />
                Acessar perfil
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            ) : undefined
          }
        />
      </div>
    </DetailSidePanelSection>
  );
};

export const CompanyAddressInfo = ({ record }: { record: Company }) => {
  const refresh = useRefresh();

  return (
    <DetailSidePanelSection
      title="Endereço & Localização"
      icon={<MapPin className="w-4 h-4 text-amber-500" />}
      defaultOpen={true}
    >
      <div className="space-y-1">
        <InlineEditField
          label="Logradouro"
          field="address"
          value={record.address}
          resource="companies"
          recordId={record.id}
          previousData={record}
          onSaved={refresh}
        />

        <InlineEditField
          label="Cidade"
          field="city"
          value={record.city}
          resource="companies"
          recordId={record.id}
          previousData={record}
          onSaved={refresh}
        />

        <InlineEditField
          label="Estado (UF)"
          field="state_abbr"
          value={record.state_abbr}
          resource="companies"
          recordId={record.id}
          previousData={record}
          onSaved={refresh}
        />

        <InlineEditField
          label="CEP"
          field="zipcode"
          value={record.zipcode}
          resource="companies"
          recordId={record.id}
          previousData={record}
          onSaved={refresh}
        />

        <InlineEditField
          label="País"
          field="country"
          value={record.country}
          resource="companies"
          recordId={record.id}
          previousData={record}
          onSaved={refresh}
        />
      </div>
    </DetailSidePanelSection>
  );
};

export const CompanyContextInfo = ({ record }: { record: Company }) => {
  const refresh = useRefresh();
  const { companySectors } = useConfigurationContext();
  const translate = useTranslate();

  const sectorChoices = companySectors.map((s) => ({
    value: s.value,
    label: s.label,
  }));

  const sizeChoices = sizes.map((s) => ({
    value: String(s.id),
    label: getTranslatedCompanySizeLabel(s, translate),
  }));

  return (
    <DetailSidePanelSection
      title="Dados Cadastrais & Porte"
      icon={<Building2 className="w-4 h-4 text-blue-500" />}
      defaultOpen={true}
    >
      <div className="space-y-1">
        <InlineEditField
          label="Setor"
          field="sector"
          value={record.sector}
          resource="companies"
          recordId={record.id}
          previousData={record}
          type="select"
          choices={sectorChoices}
          onSaved={refresh}
          displayValue={
            record.sector ? (
              <span className="font-medium text-foreground bg-primary/10 text-primary px-2 py-0.5 rounded text-[11px]">
                {companySectors.find((s) => s.value === record.sector)?.label ||
                  record.sector}
              </span>
            ) : undefined
          }
        />

        <InlineEditField
          label="CNPJ"
          field="tax_identifier"
          value={record.tax_identifier}
          resource="companies"
          recordId={record.id}
          previousData={record}
          onSaved={refresh}
          displayValue={
            record.tax_identifier ? (
              <span className="font-mono font-medium text-foreground bg-muted px-1.5 py-0.5 rounded text-[11px]">
                {record.tax_identifier}
              </span>
            ) : undefined
          }
        />

        <InlineEditField
          label="Porte"
          field="size"
          value={record.size ? String(record.size) : ""}
          resource="companies"
          recordId={record.id}
          previousData={record}
          type="select"
          choices={sizeChoices}
          onSaved={refresh}
          displayValue={
            record.size ? (
              <span className="font-medium text-foreground">
                {sizes.find((s) => s.id === record.size)
                  ? getTranslatedCompanySizeLabel(
                      sizes.find((s) => s.id === record.size)!,
                      translate,
                    )
                  : record.size}
              </span>
            ) : undefined
          }
        />

        <InlineEditField
          label="Faturamento"
          field="revenue"
          value={record.revenue}
          resource="companies"
          recordId={record.id}
          previousData={record}
          onSaved={refresh}
          displayValue={
            record.revenue ? (
              <span className="flex items-center gap-1 font-medium text-foreground">
                <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                {record.revenue}
              </span>
            ) : undefined
          }
        />
      </div>
    </DetailSidePanelSection>
  );
};

export const CompanyAdditionalInfo = ({ record }: { record: Company }) => {
  const refresh = useRefresh();
  const [locale = "en"] = useLocaleState();
  const { identity } = useGetIdentity();
  const isCurrentUser = record.sales_id === identity?.id;
  const salesName = useGetSalesName(record.sales_id, {
    enabled: !isCurrentUser,
  });

  const getBaseURL = (url: string) => {
    try {
      const urlObject = new URL(
        url.startsWith("http") ? url : `https://${url}`,
      );
      return urlObject.hostname;
    } catch {
      return url;
    }
  };

  return (
    <DetailSidePanelSection
      title="Informações Adicionais"
      icon={<Info className="w-4 h-4 text-purple-500" />}
      defaultOpen={false}
    >
      <div className="space-y-1">
        <InlineEditField
          label="Descrição"
          field="description"
          value={record.description}
          resource="companies"
          recordId={record.id}
          previousData={record}
          type="textarea"
          onSaved={refresh}
          displayValue={
            record.description ? (
              <p className="text-xs text-foreground leading-relaxed whitespace-pre-wrap">
                {record.description}
              </p>
            ) : undefined
          }
        />

        {record.context_links && record.context_links.length > 0 && (
          <div className="py-1.5 border-b border-border/40">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold block mb-1">
              Links Relevantes
            </span>
            <div className="flex flex-col gap-1">
              {record.context_links.map((link, index) =>
                link ? (
                  <a
                    key={index}
                    className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                    href={link.startsWith("http") ? link : `https://${link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {getBaseURL(link)}
                  </a>
                ) : null,
              )}
            </div>
          </div>
        )}

        {record.sales_id && (
          <div className="flex items-center justify-between gap-2 py-1.5 border-b border-border/40">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
              <UserCheck className="w-3.5 h-3.5" />
              Responsável
            </span>
            <span className="text-xs font-medium text-foreground">
              {isCurrentUser ? "Você" : salesName || record.sales_id}
            </span>
          </div>
        )}

        {record.created_at && (
          <div className="flex items-center justify-between gap-2 py-1.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Cadastrado em
            </span>
            <span className="text-xs text-muted-foreground">
              {formatLocalizedDate(record.created_at, locale)}
            </span>
          </div>
        )}
      </div>
    </DetailSidePanelSection>
  );
};

export {
  CompanyContactInfo as CompanyInfo,
  CompanyAddressInfo as AddressInfo,
  CompanyContextInfo as ContextInfo,
  CompanyAdditionalInfo as AdditionalInfo,
};
